import { container_classname } from "@components/new-ui/constants";
import NewLayout from "@components/new-ui/Layout";
import Image from "next/image";
import React from "react";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { MainContent, PressReleaseSummary } from "./news/gallabox-raises-3.5-million-to-fuel-its-mission";

const content1: PressReleaseSummary[] = [
    {
        description: (
            <p>
                Meta is fundamentally changing how businesses pay for WhatsApp messaging. From July 1, 2025, the WhatsApp Business Platform will shift from charging per
                conversation to charging per message. This will be their biggest pricing restructure since WhatsApp introduced conversation-based pricing in February 2022. <br />
                <br />
                If you&apos;re currently using WhatsApp Business API or planning to integrate it, this change will directly impact your messaging costs and strategy. Let&apos;s
                break down exactly what&apos;s changing and what it means for your business.
            </p>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">The big shift from per-conversation to per-message pricing</h2>,
        description: (
            <>
                Since February 2022, WhatsApp has charged businesses based on &apos;conversations&apos; – 24-hour messaging windows. Here&apos;s how it worked:
                <li>When you sent a template message to a customer, it opened a 24-hour conversation window.</li>
                <li>During this window, you could exchange unlimited messages for a single charge.</li>
                <li>Different conversation types (marketing, utility, authentication, service) had different rates and the rates were based on an entire single conversation.</li>
                <li>If you sent a different type of template during an active conversation, it could trigger a new conversation and additional charges.</li>
            </>
        ),
    },
    {
        title: <h3 className="text-xl font-semibold pb-2">What changes from July 1, 2025</h3>,
        description: (
            <>
                Starting July 1, 2025, you&apos;ll pay for each individual template message you send, not for conversation windows. Here&apos;s the new structure:
                <li>Marketing templates: Charged per delivered message</li>
                <li>Authentication templates: Charged per delivered message</li>
                <li>Utility templates: Charged per delivered message (but free within customer service windows)</li>
                <li>Free-form messages: Always free within customer service windows</li>
                <Image width={500} height={500} src="https://assets.gallabox.com/gb-home/press/marketing-charge.png" alt="marketing-charge" className="pt-6 w-full" />
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">The customer service window: Your key to free messaging </h2>,
        description: (
            <p>
                The 24-hour customer service window remains central to WhatsApp pricing, but with an important update.You will again be charged per message (with the exception of
                utility), instead of a conversation that can last the entire 24-hour timeline.
            </p>
        ),
    },
    {
        title: <h3 className="text-xl font-semibold">How the customer service window works</h3>,
        description: (
            <>
                <ol className="mb-5">
                    <li>A customer messages your business</li>
                    <li>A 24-hour timer starts (and resets with each new customer message)</li>
                    <li>
                        During this window, you can send:
                        <ul>
                            <li>Free-form messages (always free)</li>
                            <li>Utility template messages (free starting July 1, 2025)</li>
                        </ul>
                    </li>
                </ol>
                <p className="my-3">Let&apos;s say you run an e-commerce store. A customer messages you asking about their order status at 2 PM on Monday.</p>
            </>
        ),

        list: [
            {
                title: " Before July 1, 2025:",
                icon: <IoIosCheckmarkCircle color="red" />,
                description: [{ item: "Free-form response: Free" }, { item: "Utility template with order details: Charged (part of utility conversation)" }],
                customeStyle: "bg-red-50 border-red-500",
            },
            {
                title: "After July 1, 2025:",
                icon: <IoIosCheckmarkCircle color="green" />,
                description: [{ item: "Free-form response: Free" }, { item: "Utility template with order details: Free (within customer service window)" }],
                customeStyle: "bg-green-50 border-green-400",
            },
        ],
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Volume-based pricing tiers: How they actually work</h2>,
        description: (
            <>
                <p>
                    WhatsApp offers volume discounts based on the number of messages your business sends. The discount is tiered for different volumes depending on the region of
                    operation.
                </p>
                <h3 className="text-xl font-semibold my-3"> Tier structure</h3>
                <ul>
                    <li>Tier 1: Base rate (no discount)</li>
                    <li>Tier 2: 5% discount</li>
                    <li>Tier 3: 10% discount</li>
                    <li>Tier 4: 15% discount</li>
                    <li>Tier 5: 20% discount</li>
                </ul>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Important details about volume-based pricing tiers</h2>,
        description: (
            <ul className="gap-3">
                <li>Tiers are market and category specific: Your tier for utility messages in Brazil is separate from your tier for authentication messages in India.</li>
                <li>
                    Discounts apply only within tiers: If you send 10,000 messages and reach Tier 3, only the messages that come between Tier 3 and Tier 4 get the 10% discount, not
                    all 10,000.
                </li>
                <li> For example, 12,000 messages sent would mean that you’re in Tier 3. But the 10% discount will apply only for the 2000 new messages.</li>

                <li>Tier thresholds vary by country: The number of messages needed to reach each tier differs by market and message category.</li>
            </ul>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Free entry points are still valuable under the new pricing</h2>,
        description: (
            <>
                <p className="mb-5"> When users click on your Meta ads or your Facebook page`&apos;s click-to-chat buttons, you need to make the most of them.</p>
                <ul>
                    <li>User clicks your WhatsApp ad</li>
                    <li>You respond within 24 hours</li>
                    <li>This opens a 72-hour &quot;free entry point&quot; window</li>
                    <li>All template messages during this window are free</li>
                </ul>
                <p className="mt-5">
                    This remains unchanged with the new pricing model,
                    <a href="https://gallabox.com/click-to-whatsapp-ads" className="text-blue-700 px-1">
                        making Click-to-WhatsApp ads
                    </a>
                    an even more attractive option for businesses planning multi-message sequences.
                </p>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">How the new pricing can impact a real-world business</h2>,
        description: (
            <>
                <p>Let&apos;s examine how different businesses might be affected with a few examples by industry.</p>
                <h3 className="text-xl font-semibold my-3">1. Online retailer</h3>
            </>
        ),
        list: [
            {
                title: "Previous model (conversation-based):",
                icon: <IoIosCheckmarkCircle color="gray" />,
                description: [
                    { item: "Customer asks about order → Opens up a service conversation (which has been free since Nov 1, 2024)" },
                    { item: "Send utility template for order confirmation → Opens separate utility conversation (free)" },
                    { item: "Send more utility templates within 24 hours → Part of same utility conversation (no additional charge)" },
                ],
                customeStyle: "bg-gray-100 border-gray-400",
            },
            {
                title: "New model (message-based):",
                icon: <IoIosCheckmarkCircle color="blue" />,
                description: [
                    { item: "Customer asks about order → Customer service window opens" },
                    { item: "Send multiple utility templates within a 24-hour window" },
                    { item: "Send utility template next day (outside window) = 1 utility charge" },
                ],
                customeStyle: "bg-blue-100 border-blue-400",
            },
        ],
    },
    {
        description: <h3 className="text-xl font-semibold my-3">2. Financial services</h3>,
        list: [
            {
                title: "Previous model (conversation-based):",
                icon: <IoIosCheckmarkCircle color="gray" />,
                description: [
                    { item: "Send OTP for login → Opens 24-hour authentication conversation (charged once)" },
                    { item: "Send 5 more OTPs within same 24 hours → All part of same conversation (no additional charges)" },
                    { item: "Send OTP after 24 hours → New authentication conversation (charged again)" },
                ],
                customeStyle: "bg-gray-100 border-gray-400",
            },
            {
                title: "New model (message-based):",
                icon: <IoIosCheckmarkCircle color="blue" />,
                description: [
                    { item: "Send OTP for login → 1 authentication charge" },
                    { item: "Send another OTP (anytime) → Another authentication charge" },
                    { item: "Every OTP = Separate charge (even if sent minutes apart)" },
                ],
                customeStyle: "bg-blue-100 border-blue-400",
            },
        ],
    },
    {
        description: <h3 className="text-xl font-semibold mt-3">3. Travel agency</h3>,
        list: [
            {
                title: "Previous model (conversation-based):",
                icon: <IoIosCheckmarkCircle color="gray" />,

                description: [
                    { item: "Send promotional package → Opens marketing conversation (charged)" },
                    { item: "Customer inquires about package → Opens service conversation (free after Nov 1, 2024)" },
                    { item: "Send booking confirmation template → Opens utility conversation (charged)" },
                    { item: "Send check-in reminder + gate change + boarding pass = Same utility conversation (no extra charge if within 24 hours)" },
                ],
                customeStyle: "bg-gray-100 border-gray-400",
            },
            {
                title: "New model (message-based):",
                icon: <IoIosCheckmarkCircle color="blue" />,
                description: [
                    { item: "Send promotional package → 1 marketing charge" },
                    { item: "Customer inquires → Customer service window opens" },
                    { item: "Send booking confirmation within window → Free" },
                    { item: "Send check-in reminder (2 days later) → 1 utility charge" },
                    { item: "Send gate change + boarding pass → 2 utility charges" },
                ],
                customeStyle: "bg-blue-100 border-blue-400",
            },
        ],
    },
    {
        description: <h3 className="text-xl font-semibold my-3">4. Real estate agency</h3>,
        list: [
            {
                title: "Current model (conversation-based):",
                icon: <IoIosCheckmarkCircle color="gray" />,

                description: [
                    { item: "Send new property listing → Opens marketing conversation (charged)" },
                    { item: "Send 10 more listings within 24 hours → Same conversation (no extra charge)" },
                    { item: "Client asks about viewing → Opens service conversation (free)" },
                    { item: "Send viewing confirmation template → Opens utility conversation (charged)" },
                ],
                customeStyle: "bg-gray-100 border-gray-400",
            },
            {
                title: "New model (message-based):",
                icon: <IoIosCheckmarkCircle color="blue" />,
                description: [
                    { item: "Send property listing → 1 marketing charge" },
                    { item: "Send 10 more listings → 10 more marketing charges" },
                    { item: "Client asks about viewing → Customer service window opens" },
                    { item: "Send viewing confirmation within window → Free" },
                    { item: "Send reminder next day → 1 utility charge" },
                ],
                customeStyle: "bg-blue-100 border-blue-400",
            },
        ],
    },
    {
        description: <h3 className="text-xl font-semibold my-3">5. Healthcare provider</h3>,
        list: [
            {
                title: "Previous model (conversation-based):",
                icon: <IoIosCheckmarkCircle color="gray" />,
                description: [
                    { item: "Patient messages for appointment → Opens service conversation (free)" },
                    { item: "Send appointment confirmation template → Opens utility conversation (charged)" },
                    { item: "Send pre-appointment instructions + reminder = Same utility conversation (no extra charge if within 24 hours)" },
                    { item: "Send OTP for patient portal → Opens authentication conversation (charged)" },
                ],
                customeStyle: "bg-gray-100 border-gray-400",
            },
            {
                title: "New model (message-based):",
                icon: <IoIosCheckmarkCircle color="blue" />,
                description: [
                    { item: "Patient messages for appointment → Customer service window opens" },
                    { item: "Send appointment confirmation within window → Free" },
                    { item: "Send reminder 3 days before appointment → 1 utility charge" },
                    { item: "Send pre-appointment instructions → 1 utility charge" },
                    { item: "Send OTP for patient portal → 1 authentication charge" },
                ],
                customeStyle: "bg-blue-100 border-blue-400",
            },
        ],
    },
    {
        description: <h3 className="text-xl font-semibold my-3">6. Educational institution</h3>,
        list: [
            {
                title: "Previous model (conversation-based):",
                icon: <IoIosCheckmarkCircle color="gray" />,
                description: [
                    { item: "Send course promotion → Opens marketing conversation (charged)" },
                    { item: "Student inquires about course → Opens service conversation (free)" },
                    { item: "Send enrollment confirmation → Opens utility conversation (charged)" },
                    { item: "Send class schedule + fee reminder + exam notification = Same utility conversation (if within 24 hours)" },
                ],
                customeStyle: "bg-gray-100 border-gray-400",
            },
            {
                title: "New model (message-based):",
                icon: <IoIosCheckmarkCircle color="blue" />,
                description: [
                    { item: "Send course promotion → 1 marketing charge" },
                    { item: "Student inquires → Customer service window opens" },
                    { item: "Send enrollment confirmation within window → Free" },
                    { item: "Send class schedule (next week) → 1 utility charge" },
                    { item: "Send fee reminder → 1 utility charge" },
                    { item: "Send exam notification → 1 utility charge" },
                ],
                customeStyle: "bg-blue-100 border-blue-400",
            },
        ],
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">What this means for your messaging strategy</h2>,
        description: (
            <>
                <p className="mb-3">
                    The shift to per-message pricing requires rethinking your approach. You need to think about communicating more within a single template message and reduce the
                    back-and-forth.
                </p>
                <h3 className="text-xl font-semibold my-3"> Winners in the new model </h3>
                <ul>
                    <li>Businesses with high customer engagement: More customer-initiated conversations mean more free utility templates.</li>
                    <li>Support-heavy operations: All service conversations remain free.</li>
                    <li>Businesses using WhatsApp ads: Free entry points become even more valuable.</li>
                </ul>
                <h3 className="text-xl font-semibold my-3">Those who need to adapt</h3>
                <ul>
                    <li>Broadcast-heavy senders: Each marketing message now incurs a separate charge.</li>
                    <li>Authentication-heavy services: Multiple OTPs within 24 hours now cost more.</li>
                    <li>Businesses sending multiple template types: No more bundling different templates in one conversation.</li>
                </ul>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Practical steps you can take to prepare for the transition</h2>,
        description: (
            <>
                <p className="mb-3">
                    Here are some of the steps you can take to ensure that per-message charges and other July 1 pricing updates don’t affect your existing WhatsApp Business API
                    setup and budgets.
                </p>

                <h3 className="text-xl font-semibold mt-7 mb-3">1. Audit your current usage</h3>
                <ul>
                    <li>Calculate how many template messages you send (not just conversations)</li>
                    <li>Identify what percentage fall within customer service windows</li>
                    <li>Review your authentication message frequency</li>
                </ul>
                <h3 className="text-xl font-semibold mt-7 mb-3"> 2. Optimize message timing</h3>
                <ul>
                    <li>
                        Encourage customers to initiate conversations (
                        <a href="https://gallabox.com/whatsapp-qr-code-generator" className="text-blue-700">
                            WhatsApp QR codes,
                        </a>
                        web widgets, WhatsApp links, etc.,)
                    </li>
                    <li>Batch utility messages within customer service windows when possible</li>
                    <li>Consider shifting some templates to free-form messages</li>
                </ul>
                <h3 className="text-xl font-semibold mt-7 mb-3">3. Review your templates</h3>
                <ul>
                    <li>Ensure utility templates are properly categorized</li>
                    <li>Consider which messages truly need to be templates</li>
                    <li>Optimize template content for clarity and effectiveness</li>
                </ul>
                <h3 className="text-xl font-semibold mt-7 mb-3"> 4. Calculate projected costs</h3>
                <ul>
                    <li>Use Meta`&apos;s pricing calculator with your actual message volumes</li>
                    <li>Factor in your market-specific rates</li>
                    <li>Consider volume tier thresholds for your primary markets</li>
                </ul>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Optimize your WhatsApp Business strategy for customer-initiated conversations and free entry-points</h2>,
        description: (
            <p>
                While the per-message pricing simplifies understanding costs (you pay for what you send), it may increase expenses for some use cases while reducing them for
                others. <br />
                <br />
                The key to managing costs in this new model lies in maximizing customer-initiated conversations and strategically timing your template messages. Businesses that
                adapt their messaging patterns to work with, rather than against, these changes will find opportunities to maintain or even reduce their WhatsApp messaging costs.
            </p>
        ),
    },
];

const WhatsappBusinessPricingArticle: React.FC = () => {
    return (
        <NewLayout
            title="WhatsApp Business Pricing Changes 1 July 2025"
            description="Here’s everything you need to know and plan for the WhatsApp Business platform’s July 1, 2025 pricing changes."
            canonical="https://gallabox.com/news/whatsapp-coexistence-update"
        >
            <section className="pb-20" style={{ background: "linear-gradient(265deg, #A1D8FF -7.73%, #396AFF 19.04%, #00238D 61.31%, #000 102.16%)" }}>
                <div className={`${container_classname}  font-bold text-white md:h-96 md:pt-40 pt-16 px-6`}>
                    <p className="text-white text-sm font-normal">UPDATES</p>
                    <h1 style={{ fontFamily: "Degular Variable" }} className="lg:text-7xl md:text-5xl text-4xl">
                        July 1, 2025: Every WhatsApp Business pricing change you need to know
                    </h1>
                    <p className="text-white text-sm font-normal mt-3">July 1, 2025</p>
                </div>
            </section>
            <section className="pt-20 pb-20">
                <MainContent aboutData={content1} />
            </section>
        </NewLayout>
    );
};

export default WhatsappBusinessPricingArticle;
